import { useState, useEffect, useMemo, useCallback, useReducer, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import { s, authHeaders, Toast, getODataRows } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { useDistributorScope, ALL_VENDORS_OPTION_LABEL } from '../hooks/useDistributorScope';
import {
  SOURCE_FIELDS, DOC_TYPES, PAPER_SIZES, paperDims, emptyLayout,
} from './belgeDizayni/fieldCatalog';
import { printLayout, resolveBarcodeValue, resolveElementValue } from './belgeDizayni/printRenderer';
import { barcodeHtml, BARCODE_FORMATS } from './belgeDizayni/barcode';
import { AGG_FUNCS, AGG_SHORT } from './belgeDizayni/calc';
import { AiDesignPanel } from './belgeDizayni/AiDesignPanel';
import { DesignerGhostOverlay } from './belgeDizayni/DesignerGhostOverlay';
import { XsltImportDialog } from './belgeDizayni/XsltImportDialog';
import {
  st, pf, plabel, pin, psel, toggleBtn, swatch, alignBtn, sectionTitle, sectionWrap,
  EL_RESIZE_STYLES, LINE_ENABLE_RESIZE, LINE_RESIZE_STYLES,
} from './belgeDizayni/designerStyles';
import {
  PX_PER_MM, round, SNAP_PX, collectSnapTargets, computeSnap, computeResizeSnap, snapToGrid,
} from './belgeDizayni/geometry';
import { useCanvasZoom } from './belgeDizayni/useCanvasZoom';
import { RulerTop, RulerLeft } from './belgeDizayni/Rulers';
import { GridOverlay } from './belgeDizayni/GridOverlay';
import { SmartGuides } from './belgeDizayni/SmartGuides';
import { LayersPanel } from './belgeDizayni/LayersPanel';
import { Segmented } from './belgeDizayni/Segmented';
import { elementIcon } from './belgeDizayni/elementUtils';
import { toFront, toBack, bringForward, sendBackward, reorderTo } from './belgeDizayni/zorder';

const API_BASE = '/api/documenttemplates';

const DOC_ENDPOINT = { 0: '/api/orderheaders', 1: '/api/waybills', 2: '/api/invoices', 3: '/api/financetransactions' };
const DOC_ORDERBY = { 0: 'DocumentDate desc', 1: 'DocumentDate desc', 2: 'DocumentDate desc', 3: 'TransactionDate desc' };

function uid() {
  return window.crypto?.randomUUID?.() || `el-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const ALIGNS = [{ value: 'left', label: 'Sol' }, { value: 'center', label: 'Orta' }, { value: 'right', label: 'Sağ' }];

// Sol palet "Ekle" kartları (ikon elementUtils'ten çözülür).
const TOOL_ITEMS = [
  { kind: 'static-text', label: 'Metin' },
  { kind: 'line', label: 'Çizgi' },
  { kind: 'box', label: 'Kutu' },
  { kind: 'barcode', label: 'Barkod' },
  { kind: 'image', label: 'Resim' },
  { kind: 'calc', label: 'Hesap', title: 'Detay toplamı (Σ/say/ortalama)' },
  { kind: 'amount-words', label: 'Yazıyla', title: 'Tutarı yazıyla (TL)' },
];

const HISTORY_LIMIT = 100;

// Geri/ileri al destekli layout history reducer'ı (saf — strict-mode'da güvenli).
function historyReducer(state, action) {
  switch (action.type) {
    case 'reset':
      return { present: action.layout, past: [], future: [] };
    case 'commit': {
      const next = typeof action.updater === 'function' ? action.updater(state.present) : action.updater;
      if (next === state.present) return state;
      return { present: next, past: [...state.past, state.present].slice(-HISTORY_LIMIT), future: [] };
    }
    case 'undo': {
      if (!state.past.length) return state;
      const prev = state.past[state.past.length - 1];
      return { present: prev, past: state.past.slice(0, -1), future: [state.present, ...state.future].slice(0, HISTORY_LIMIT) };
    }
    case 'redo': {
      if (!state.future.length) return state;
      const next = state.future[0];
      return { present: next, past: [...state.past, state.present].slice(-HISTORY_LIMIT), future: state.future.slice(1) };
    }
    default:
      return state;
  }
}

export function BelgeDizayniDesignerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  usePageMeta('Belge Dizaynı', 'Sürükle-bırak ile belge düzeni tasarlayın.');
  // Menü aksiyon hakkı: tasarımcı hep var olan bir dizaynı açar → LayoutJson kaydetmek Güncelleme hakkı ister.
  // Tuval üzerindeki düzenleme (öğe ekle/taşı/sil, AI, XSLT) yalnız yerel durumu değiştirir; kalıcı değil.
  const { canEdit } = usePageActions();

  const [tpl, setTpl] = useState(null);       // meta (name, docType, target, vendorId...)

  // Talep 834 — dizaynın distribütör kapsamı (toolbar'daki "Distribütör" seçici).
  const { vendorOptions, isDistributorUser } = useDistributorScope();
  // Distribütör tipi kullanıcıda seçici salt-okunur → listede YALNIZ kaydın kendi değeri durmalı.
  // vendorOptions'ı olduğu gibi verirsek, GENEL (vendorId=null) bir dizaynda tarayıcı ilk seçeneği
  // gösterir ve kullanıcı dizaynı kendi distribütörüne aitmiş gibi okur.
  const scopeOptions = useMemo(() => {
    if (!isDistributorUser) return vendorOptions;
    const cur = String(tpl?.vendorId || '');
    return [vendorOptions.find((o) => o.value === cur) || { value: cur, label: cur || ALL_VENDORS_OPTION_LABEL }];
  }, [isDistributorUser, vendorOptions, tpl]);

  const [hist, dispatch] = useReducer(historyReducer, { present: null, past: [], future: [] });
  const layout = hist.present;                // LayoutJson parsed (history reducer'ın 'present'i)
  const canUndo = hist.past.length > 0;
  const canRedo = hist.future.length > 0;
  // Mevcut tüm setLayout((l)=>...) çağrıları değişmeden çalışsın diye commit'e köprülenir.
  const setLayout = useCallback((updater) => dispatch({ type: 'commit', updater }), []);
  const resetLayout = useCallback((l) => dispatch({ type: 'reset', layout: l }), []);
  const [sel, setSel] = useState(null);       // { kind:'element'|'band'|'bandcol'|'page', id? }
  const [selIds, setSelIds] = useState([]);   // çoklu seçilen eleman id'leri (sel.id = birincil/anchor)

  const undo = useCallback(() => { dispatch({ type: 'undo' }); setSelIds([]); setSel({ kind: 'page' }); }, []);
  const redo = useCallback(() => { dispatch({ type: 'redo' }); setSelIds([]); setSel({ kind: 'page' }); }, []);

  // Tuval referansları + zoom/pan hook
  const rootRef = useRef(null);
  const [rootH, setRootH] = useState(null); // tasarımcıyı tam ekrana sığdır (pencere scroll'u olmasın)
  const canvasWrapRef = useRef(null);
  const pageRef = useRef(null);
  const earlyDims = layout
    ? paperDims(layout.page.paperSize, layout.page.orientation, layout.page.widthMm, layout.page.heightMm)
    : { widthMm: 210, heightMm: 297 };
  const { zoom, setZoom, fitToScreen, zoomTo100, spaceHeld, panning, onPanMouseDown } = useCanvasZoom(canvasWrapRef, earlyDims.widthMm, earlyDims.heightMm);

  // Akıllı kılavuz + grid + sekme + cetvel
  const [drag, setDrag] = useState(null);        // {id, x, y, w, h, snapped} — sürükleme sırasında canlı (mm)
  const [guides, setGuides] = useState([]);      // [{axis, pos}] printable-relative mm
  const [showGrid, setShowGrid] = useState(false);
  const [snapGrid, setSnapGrid] = useState(false);
  const [gridMm, setGridMm] = useState(5);
  const [sideTab, setSideTab] = useState('elements'); // 'elements' | 'layers'
  const [rulerOrigin, setRulerOrigin] = useState({ x: 0, y: 0 });
  const clipboardRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Önizleme
  const [previewOpen, setPreviewOpen] = useState(false);
  const [docList, setDocList] = useState([]);
  const [previewDocId, setPreviewDocId] = useState('');

  // XSLT içe aktarma diyaloğu (Talep #153)
  const [xsltOpen, setXsltOpen] = useState(false);

  // AI tasarım paneli
  const [aiOpen, setAiOpen] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);   // yanıt bekleniyor (ilk token öncesi)
  const [livePreview, setLivePreview] = useState(null);  // akarken canlı çizilen kısmi layout

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // AI'ın ürettiği layout'u tuvale uygula (geri-al destekli — commit).
  const applyAiLayout = useCallback((next) => {
    setLayout((l) => {
      const page = { ...(l?.page || {}), ...(next.page || {}) };
      const dims = paperDims(page.paperSize, page.orientation, page.widthMm, page.heightMm);
      page.widthMm = dims.widthMm; page.heightMm = dims.heightMm;
      if (!page.margins) page.margins = { top: 10, right: 10, bottom: 10, left: 10 };
      return {
        schemaVersion: 1,
        page,
        elements: Array.isArray(next.elements) ? next.elements : (l?.elements || []),
        detailBand: next.detailBand || l?.detailBand || { enabled: false, source: 'InvoiceDetail', x: 0, y: 60, width: dims.widthMm - 20, rowHeight: 7, showHeader: true, headerHeight: 7, showFooter: false, columns: [] },
        footer: next.footer || l?.footer || { elements: [] },
      };
    });
    setSelIds([]); setSel({ kind: 'page' });
  }, [setLayout]);

  // Akarken gelen kısmi layout'u canlı önizle (geçmişe yazmaz; sadece görsel).
  const onAiLivePreview = useCallback((partial) => {
    setLivePreview((prev) => {
      const base = prev || {};
      return {
        schemaVersion: 1,
        page: partial.page || base.page,
        elements: partial.elements || base.elements || [],
        detailBand: partial.detailBand || base.detailBand,
        footer: { elements: [] },
      };
    });
  }, []);

  // Akış bitince tam layout'u tek (geri-alınabilir) adımda uygula ve canlı önizlemeyi kaldır.
  const onAiFinal = useCallback((finalLayout) => {
    applyAiLayout(finalLayout);
    setLivePreview(null);
    showToast('success', 'Tasarım uygulandı (geri almak için Ctrl+Z)');
  }, [applyAiLayout]);

  // XSLT'ten üretilen öğe + detay bandını tuvale yerleştir (geri-al destekli — mevcut yerine).
  const applyXsltImport = useCallback((elements, detailBand, meta) => {
    setLayout((l) => ({
      ...l,
      elements,
      detailBand: detailBand ? { ...(l.detailBand || {}), ...detailBand } : (l.detailBand || {}),
    }));
    setSelIds([]); setSel({ kind: 'page' });
    const parts = [`${elements.length} öğe`];
    if (meta?.boundCount) parts.push(`${meta.boundCount} alan bağlandı`);
    if (meta?.hasDetail) parts.push(`detay bandı (${meta.detailCols} kolon)`);
    showToast('success', `XSLT içe aktarıldı — ${parts.join(', ')} (geri almak için Ctrl+Z)`);
  }, [setLayout]);

  // Yükle
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Dizayn yüklenemedi');
        const json = await res.json();
        const data = json?.data ?? json?.Data ?? json;
        if (!alive) return;
        setTpl(data);
        let parsed;
        try { parsed = data.layoutJson ? JSON.parse(data.layoutJson) : null; } catch { parsed = null; }
        if (!parsed) parsed = emptyLayout(data.paperSize || 'A4', data.orientation || 0);
        // Sayfa boyutunu meta ile senkronla.
        const dims = paperDims(parsed.page.paperSize, parsed.page.orientation, parsed.page.widthMm, parsed.page.heightMm);
        parsed.page.widthMm = dims.widthMm; parsed.page.heightMm = dims.heightMm;
        resetLayout(parsed);
      } catch (e) {
        showToast('error', e.message || 'Yükleme hatası');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, resetLayout]);

  // Layout mutasyon yardımcıları
  const patchPage = useCallback((patch) => {
    setLayout((l) => {
      const page = { ...l.page, ...patch };
      if (patch.margins) page.margins = { ...l.page.margins, ...patch.margins };
      const dims = paperDims(page.paperSize, page.orientation, page.widthMm, page.heightMm);
      page.widthMm = dims.widthMm; page.heightMm = dims.heightMm;
      return { ...l, page };
    });
  }, [setLayout]);

  const updateElement = useCallback((elId, patch) => {
    setLayout((l) => ({ ...l, elements: l.elements.map((e) => (e.id === elId ? { ...e, ...patch } : e)) }));
  }, [setLayout]);

  const updateBand = useCallback((patch) => {
    setLayout((l) => ({ ...l, detailBand: { ...l.detailBand, ...patch } }));
  }, [setLayout]);

  const updateBandCol = useCallback((colId, patch) => {
    setLayout((l) => ({
      ...l,
      detailBand: { ...l.detailBand, columns: l.detailBand.columns.map((c) => (c.id === colId ? { ...c, ...patch } : c)) },
    }));
  }, [setLayout]);

  // --- Seçim ---
  const selectElement = useCallback((elId, additive) => {
    setSelIds((prev) => {
      let next;
      if (additive) next = prev.includes(elId) ? prev.filter((x) => x !== elId) : [...prev, elId];
      else next = [elId];
      const primary = next.includes(elId) ? elId : (next[next.length - 1] ?? null);
      setSel(primary ? { kind: 'element', id: primary } : { kind: 'page' });
      return next;
    });
  }, []);

  const onElementMouseDown = (e, elId) => {
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    // Çoklu seçimin parçası olan bir öğeye düz tıklama → seçimi koru (grubu sürüklemek için).
    if (!additive && selIds.length > 1 && selIds.includes(elId)) {
      setSel({ kind: 'element', id: elId });
      return;
    }
    selectElement(elId, additive);
  };

  const clearSelection = useCallback(() => { setSelIds([]); setSel({ kind: 'page' }); }, []);

  // Snap hedefleri: akıllı kılavuz açıkken diğer öğelere/kenara/merkeze, kapalıyken grid'e.
  const snapPos = useCallback((el, rawX, rawY, k) => {
    if (snapGrid) return { x: snapToGrid(rawX, gridMm), y: snapToGrid(rawY, gridMm), snapped: true, guides: [] };
    const dd = paperDims(layout.page.paperSize, layout.page.orientation, layout.page.widthMm, layout.page.heightMm);
    const exclude = selIds.length ? selIds : [el.id];
    const targets = collectSnapTargets(layout, dd, exclude);
    const r = computeSnap({ x: rawX, y: rawY, w: el.width || 0, h: el.height || 0 }, targets, SNAP_PX / k);
    return { x: r.x, y: r.y, snapped: r.snapped, guides: r.guides };
  }, [snapGrid, gridMm, layout, selIds]);

  // Canlı sürükleme: snaplenmiş konumu state'e yaz → hem kılavuzları hem Rnd position'ını besler.
  const onElementDrag = useCallback((el, d) => {
    const k = PX_PER_MM * zoom;
    const r = snapPos(el, round(d.x / k), round(d.y / k), k);
    setDrag({ id: el.id, x: r.x, y: r.y, w: el.width || 0, h: el.height || 0, snapped: r.snapped });
    setGuides(r.guides);
  }, [zoom, snapPos]);

  // Bırakınca: snaplenmiş konumu kalıcı yaz; çoklu seçimde grubu aynı delta ile taşı.
  const onElementDragStop = useCallback((el, d) => {
    const k = PX_PER_MM * zoom;
    const r = snapPos(el, round(d.x / k), round(d.y / k), k);
    const nx = r.x; const ny = r.y;
    const dx = nx - el.x; const dy = ny - el.y;
    setLayout((l) => {
      const group = selIds.length > 1 && selIds.includes(el.id);
      return {
        ...l,
        elements: l.elements.map((e) => {
          if (e.id === el.id) return { ...e, x: nx, y: ny };
          if (group && selIds.includes(e.id)) return { ...e, x: round(e.x + dx), y: round(e.y + dy) };
          return e;
        }),
      };
    });
    setDrag(null); setGuides([]);
  }, [selIds, zoom, snapPos, setLayout]);

  const edgesFromDir = (dir) => {
    const d = String(dir).toLowerCase();
    return { right: d.includes('right'), left: d.includes('left'), bottom: d.includes('bottom'), top: d.includes('top') };
  };

  // Resize sırasında yalnız kılavuz göster (boyutu canlı geri beslemek react-rnd'de kırılgan).
  const onElementResize = useCallback((el, dir, ref, pos) => {
    if (snapGrid) { setGuides([]); return; }
    const k = PX_PER_MM * zoom;
    const box = { x: round(pos.x / k), y: round(pos.y / k), w: round(parseFloat(ref.style.width) / k), h: round(parseFloat(ref.style.height) / k) };
    const dd = paperDims(layout.page.paperSize, layout.page.orientation, layout.page.widthMm, layout.page.heightMm);
    const r = computeResizeSnap(box, edgesFromDir(dir), collectSnapTargets(layout, dd, [el.id]), SNAP_PX / k);
    setGuides(r.guides);
  }, [snapGrid, zoom, layout]);

  const onElementResizeStop = useCallback((el, dir, ref, pos, isLine) => {
    const k = PX_PER_MM * zoom;
    let x = round(pos.x / k); let y = round(pos.y / k);
    let w = round(parseFloat(ref.style.width) / k);
    let h = isLine ? (el.height || 0.3) : round(parseFloat(ref.style.height) / k);
    if (!snapGrid) {
      const dd = paperDims(layout.page.paperSize, layout.page.orientation, layout.page.widthMm, layout.page.heightMm);
      const r = computeResizeSnap({ x, y, w, h }, edgesFromDir(dir), collectSnapTargets(layout, dd, [el.id]), SNAP_PX / k);
      x = r.x; y = r.y; w = r.w; if (!isLine) h = r.h;
    }
    updateElement(el.id, isLine ? { width: w, x, y } : { width: w, height: h, x, y });
    setGuides([]);
  }, [snapGrid, zoom, layout, updateElement]);

  // --- Z-sıra / kilit / gizle / panoya kopyala ---
  const applyZ = useCallback((fn) => {
    const ids = selIds.length ? selIds : (sel?.kind === 'element' ? [sel.id] : []);
    if (!ids.length) return;
    setLayout((l) => ({ ...l, elements: fn(l.elements, ids) }));
  }, [selIds, sel, setLayout]);

  const toggleLock = useCallback((elId) => setLayout((l) => ({ ...l, elements: l.elements.map((e) => (e.id === elId ? { ...e, locked: !e.locked } : e)) })), [setLayout]);
  const toggleHidden = useCallback((elId) => setLayout((l) => ({ ...l, elements: l.elements.map((e) => (e.id === elId ? { ...e, hidden: !e.hidden } : e)) })), [setLayout]);
  const onLayerReorder = useCallback((dragId, targetId) => setLayout((l) => ({ ...l, elements: reorderTo(l.elements, dragId, targetId) })), [setLayout]);

  const copySel = useCallback(() => {
    const set = new Set(selIds);
    clipboardRef.current = (layout?.elements || []).filter((e) => set.has(e.id)).map((e) => { const c = { ...e }; delete c.id; return c; });
  }, [selIds, layout]);

  const pasteOrDuplicate = useCallback((items) => {
    if (!items || !items.length) return;
    const news = items.map((c) => ({ ...c, id: uid(), x: round((c.x || 0) + 4), y: round((c.y || 0) + 4) }));
    setLayout((l) => ({ ...l, elements: [...l.elements, ...news] }));
    const ids = news.map((n) => n.id);
    setSelIds(ids); setSel({ kind: 'element', id: ids[ids.length - 1] });
  }, [setLayout]);

  const pasteClip = useCallback(() => pasteOrDuplicate(clipboardRef.current), [pasteOrDuplicate]);
  const duplicateSel = useCallback(() => {
    const set = new Set(selIds);
    pasteOrDuplicate((layout?.elements || []).filter((e) => set.has(e.id)).map((e) => { const c = { ...e }; delete c.id; return c; }));
  }, [selIds, layout, pasteOrDuplicate]);

  // --- Hizalama / dağıtma / eşitleme ---
  const alignElements = useCallback((mode) => {
    setLayout((l) => {
      const ids = selIds.length ? selIds : (sel?.kind === 'element' ? [sel.id] : []);
      if (!ids.length) return l;
      const set = new Set(ids);
      const els = l.elements.filter((e) => set.has(e.id));
      if (!els.length) return l;
      const d = paperDims(l.page.paperSize, l.page.orientation, l.page.widthMm, l.page.heightMm);
      const mg = l.page.margins;
      const pw = d.widthMm - mg.left - mg.right;
      const ph = d.heightMm - mg.top - mg.bottom;
      let minX, maxX, minY, maxY;
      if (els.length >= 2) {
        minX = Math.min(...els.map((e) => e.x));
        maxX = Math.max(...els.map((e) => e.x + e.width));
        minY = Math.min(...els.map((e) => e.y));
        maxY = Math.max(...els.map((e) => e.y + (e.height || 0)));
      } else { minX = 0; maxX = pw; minY = 0; maxY = ph; }
      const anchor = els.find((e) => e.id === ids[0]) || els[0];
      const patches = new Map();
      if (mode === 'distH' && els.length >= 3) {
        const sorted = [...els].sort((a, b) => a.x - b.x);
        const first = sorted[0].x; const step = (sorted[sorted.length - 1].x - first) / (sorted.length - 1);
        sorted.forEach((e, i) => patches.set(e.id, { x: round(first + i * step) }));
      } else if (mode === 'distV' && els.length >= 3) {
        const sorted = [...els].sort((a, b) => a.y - b.y);
        const first = sorted[0].y; const step = (sorted[sorted.length - 1].y - first) / (sorted.length - 1);
        sorted.forEach((e, i) => patches.set(e.id, { y: round(first + i * step) }));
      } else {
        els.forEach((e) => {
          switch (mode) {
            case 'left': patches.set(e.id, { x: round(minX) }); break;
            case 'right': patches.set(e.id, { x: round(maxX - e.width) }); break;
            case 'hcenter': patches.set(e.id, { x: round((minX + maxX) / 2 - e.width / 2) }); break;
            case 'top': patches.set(e.id, { y: round(minY) }); break;
            case 'bottom': patches.set(e.id, { y: round(maxY - (e.height || 0)) }); break;
            case 'vcenter': patches.set(e.id, { y: round((minY + maxY) / 2 - (e.height || 0) / 2) }); break;
            case 'sameW': patches.set(e.id, { width: round(anchor.width) }); break;
            case 'sameH': patches.set(e.id, { height: round(anchor.height || 0) }); break;
            default: break;
          }
        });
      }
      return { ...l, elements: l.elements.map((e) => (patches.has(e.id) ? { ...e, ...patches.get(e.id) } : e)) };
    });
  }, [selIds, sel, setLayout]);

  const addFieldElement = (source, f) => {
    const meta = SOURCE_FIELDS[source];
    if (meta?.isDetail) { addBandColumn(source, f); return; }
    const count = (layout.elements || []).length;
    const el = {
      id: uid(), type: 'field', source, fieldKey: f.key, fieldType: f.type,
      x: 10, y: 10 + (count % 20) * 9, width: 55, height: 7,
      fontSizePt: 10, bold: false, underline: false, italic: false, align: 'left', valign: 'top', border: false,
    };
    setLayout((l) => ({ ...l, elements: [...l.elements, el] }));
    setSel({ kind: 'element', id: el.id });
    setSelIds([el.id]);
  };

  const addStatic = (kind) => {
    const count = (layout.elements || []).length;
    const base = { id: uid(), x: 10, y: 10 + (count % 20) * 9, fontSizePt: 10, bold: false, underline: false, italic: false, align: 'left', valign: 'top', border: false };
    let el;
    if (kind === 'static-text') el = { ...base, type: 'static-text', text: 'Metin', width: 50, height: 7 };
    else if (kind === 'line') el = { ...base, type: 'line', width: 80, height: 0.3 };
    else if (kind === 'image') el = { ...base, type: 'image', src: '', fit: 'contain', width: 40, height: 20, border: false };
    else if (kind === 'barcode') el = { ...base, type: 'barcode', format: 'CODE128', valueMode: 'static', text: '1234567890', source: 'Invoice', fieldKey: 'TextId', showText: true, width: 50, height: 16 };
    else if (kind === 'calc') el = { ...base, type: 'calc', aggFn: 'sum', aggField: 'NetAmount', valueType: 'money', align: 'right', width: 40, height: 7 };
    else if (kind === 'amount-words') el = { ...base, type: 'amount-words', source: 'Invoice', fieldKey: 'NetAmount', currency: 'TL', subunit: 'Kr', upper: false, width: 90, height: 10 };
    else el = { ...base, type: 'box', width: 50, height: 20 };
    setLayout((l) => ({ ...l, elements: [...l.elements, el] }));
    setSel({ kind: 'element', id: el.id });
    setSelIds([el.id]);
  };

  const addBandColumn = (source, f) => {
    setLayout((l) => {
      const band = l.detailBand;
      const cols = band.columns || [];
      const xOffset = cols.reduce((acc, c) => acc + (c.width || 0), 0);
      const col = {
        id: uid(), fieldKey: f.key, fieldType: f.type, headerText: f.label,
        x: xOffset, width: 25, fontSizePt: 9, bold: false, underline: false, align: f.type === 'money' || f.type === 'number' ? 'right' : 'left',
      };
      return { ...l, detailBand: { ...band, enabled: true, columns: [...cols, col] } };
    });
  };

  const deleteSelected = useCallback(() => {
    if (selIds.length) {
      const set = new Set(selIds);
      setLayout((l) => ({ ...l, elements: l.elements.filter((e) => !set.has(e.id)) }));
      setSelIds([]); setSel({ kind: 'page' }); return;
    }
    if (sel?.kind === 'bandcol') setLayout((l) => ({ ...l, detailBand: { ...l.detailBand, columns: l.detailBand.columns.filter((c) => c.id !== sel.id) } }));
    setSel(null);
  }, [selIds, sel, setLayout]);

  // Klavye: Delete=sil, Ok=nudge (Shift=5mm), Esc=temizle, Ctrl+A=tümünü seç, Ctrl+C/V/D=kopyala/yapıştır/çoğalt,
  // Ctrl+]/[=öne/arkaya (Shift ile en öne/en arkaya).
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      const k = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (mod && (k === 'y' || (k === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return; }
      if (mod && k === 'c' && selIds.length) { e.preventDefault(); copySel(); return; }
      if (mod && k === 'v') { e.preventDefault(); pasteClip(); return; }
      if (mod && k === 'd' && selIds.length) { e.preventDefault(); duplicateSel(); return; }
      if (mod && (e.key === ']' || e.key === '[')) {
        e.preventDefault();
        if (e.key === ']') applyZ(e.shiftKey ? toFront : bringForward);
        else applyZ(e.shiftKey ? toBack : sendBackward);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && (selIds.length || sel?.kind === 'bandcol')) {
        e.preventDefault(); deleteSelected();
      } else if (e.key === 'Escape') {
        setSelIds([]); setSel({ kind: 'page' });
      } else if (e.key.startsWith('Arrow') && selIds.length) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 0.5;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        const set = new Set(selIds);
        setLayout((l) => ({ ...l, elements: l.elements.map((el) => (set.has(el.id) && !el.locked ? { ...el, x: round(el.x + dx), y: round(el.y + dy) } : el)) }));
      } else if (mod && k === 'a' && layout) {
        e.preventDefault();
        const all = layout.elements.map((el) => el.id);
        setSelIds(all);
        setSel(all.length ? { kind: 'element', id: all[all.length - 1] } : { kind: 'page' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selIds, sel, layout, deleteSelected, undo, redo, setLayout, copySel, pasteClip, duplicateSel, applyZ]);

  const handleSave = async () => {
    if (!tpl || !layout) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Update`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          id: tpl.id,
          name: tpl.name,
          docType: tpl.docType,
          paperSize: layout.page.paperSize,
          orientation: layout.page.orientation,
          marginTop: layout.page.margins.top,
          marginRight: layout.page.margins.right,
          marginBottom: layout.page.margins.bottom,
          marginLeft: layout.page.margins.left,
          customWidthMm: layout.page.paperSize === 'Custom' ? layout.page.widthMm : null,
          customHeightMm: layout.page.paperSize === 'Custom' ? layout.page.heightMm : null,
          target: tpl.target,
          isActive: tpl.isActive ?? true,
          isDefault: tpl.isDefault ?? false,
          // Talep 834 — Update attach-all'dır: göndermezsek kaydın distribütörü null'lanır
          // (dizayn sessizce "genel"e döner). Kaydın kendi değeri her zaman geri yazılmalı.
          vendorId: tpl.vendorId || null,
          layoutJson: JSON.stringify(layout),
        }),
      });
      if (!res.ok) throw new Error('Kaydedilemedi');
      showToast('success', 'Dizayn kaydedildi');
    } catch (e) {
      showToast('error', e.message || 'Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  // Önizleme: belge listesini çek
  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewDocId('');
    try {
      const ep = DOC_ENDPOINT[tpl.docType] || DOC_ENDPOINT[2];
      const orderby = DOC_ORDERBY[tpl.docType] || 'DocumentDate desc';
      const res = await fetch(`${ep}?$top=25&$orderby=${encodeURIComponent(orderby)}`, { headers: authHeaders() });
      if (res.ok) setDocList(getODataRows(await res.json()));
    } catch { /* boş bırak */ }
  };

  const runPreview = async (print) => {
    if (!previewDocId) { showToast('error', 'Önce bir belge seçin'); return; }
    try {
      const res = await fetch(`${API_BASE}/ResolveData?docType=${tpl.docType}&documentId=${previewDocId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Belge verisi alınamadı');
      const json = await res.json();
      const data = json?.data ?? json?.Data ?? json;
      printLayout(layout, data, tpl.name || 'Belge');
      if (print) setPreviewOpen(false);
    } catch (e) {
      showToast('error', e.message || 'Önizleme hatası');
    }
  };

  const printBlank = () => printLayout(layout, null, tpl?.name || 'Belge');

  // Görüntülenen layout: AI akarken canlı önizleme, yoksa gerçek layout.
  const view = livePreview && layout
    ? {
      page: livePreview.page || layout.page,
      elements: livePreview.elements || [],
      detailBand: livePreview.detailBand || layout.detailBand,
    }
    : layout;

  // Geometri (görüntülenen layout'a göre)
  const dims = view ? paperDims(view.page.paperSize, view.page.orientation, view.page.widthMm, view.page.heightMm) : { widthMm: 210, heightMm: 297 };
  const mmToPx = (mm) => mm * PX_PER_MM * zoom;
  const pxToMm = (px) => px / (PX_PER_MM * zoom);
  const m = view?.page.margins || { top: 0, right: 0, bottom: 0, left: 0 };
  const printW = dims.widthMm - m.left - m.right;
  const printH = dims.heightMm - m.top - m.bottom;

  const selectedElement = useMemo(
    () => (sel?.kind === 'element' && layout ? layout.elements.find((e) => e.id === sel.id) : null),
    [sel, layout],
  );
  const selectedCol = useMemo(
    () => (sel?.kind === 'bandcol' && layout ? layout.detailBand.columns.find((c) => c.id === sel.id) : null),
    [sel, layout],
  );

  // Cetvel sıfır noktası = kağıdın sol/üst kenarının canvasWrap viewport'undaki konumu (scroll/zoom/resize'a duyarlı).
  const syncRulerOrigin = useCallback(() => {
    const wrap = canvasWrapRef.current; const page = pageRef.current;
    if (!wrap || !page) return;
    const wr = wrap.getBoundingClientRect(); const pr = page.getBoundingClientRect();
    setRulerOrigin({ x: pr.left - wr.left, y: pr.top - wr.top });
  }, []);
  useLayoutEffect(() => { syncRulerOrigin(); }, [zoom, dims.widthMm, dims.heightMm, loading, syncRulerOrigin]);
  useEffect(() => {
    const onResize = () => syncRulerOrigin();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncRulerOrigin]);

  // Tasarımcıyı kalan viewport yüksekliğine tam oturt → tek sayfa, pencere scroll'u yok (scroll iç panellerde).
  // Topbar gibi üst şerit async yükseklik değiştirebildiği için ResizeObserver + birkaç tick ile yeniden ölç.
  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current; if (!el) return;
      const h = Math.max(360, Math.round(window.innerHeight - el.getBoundingClientRect().top - 18)); // 18 = alt güvenlik payı
      setRootH((prev) => (prev === h ? prev : h));
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 300);
    window.addEventListener('resize', measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(document.body);
    }
    return () => {
      cancelAnimationFrame(raf); clearTimeout(t);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [loading]);

  if (loading) return <div style={{ padding: 24 }}>Yükleniyor…</div>;
  if (!layout || !tpl) return <div style={{ padding: 24 }}>Dizayn bulunamadı. <button style={s.btnSecondary} onClick={() => navigate('/belge-dizayni')}>Listeye dön</button></div>;

  const docTypeLabel = (DOC_TYPES.find((d) => d.value === tpl.docType) || {}).label || '';

  return (
    <div ref={rootRef} style={{ ...st.root, ...(rootH ? { height: rootH } : {}) }}>
      {/* Toolbar */}
      <div style={st.toolbar}>
        <button style={st.iconBtn} onClick={() => navigate('/belge-dizayni')} title="Listeye dön">← Liste</button>
        <span style={st.title}>{tpl.name}<span style={st.badge}>{docTypeLabel}</span></span>
        <span style={st.tbSep} />
        <button style={st.iconBtn} onClick={undo} disabled={!canUndo} title="Geri al (Ctrl+Z)">↶</button>
        <button style={st.iconBtn} onClick={redo} disabled={!canRedo} title="İleri al (Ctrl+Y)">↷</button>
        <span style={st.tbSep} />
        {/* Talep 834 — dizaynın distribütör kapsamı. Boş = tüm distribütörler için genel dizayn.
            Distribütör tipi kullanıcı kapsamı DEĞİŞTİREMEZ (salt-okunur): burada oto-seçim
            YAPMIYORUZ — mevcut bir GENEL dizaynı açıp kaydetmek onu sessizce tek distribütöre
            hapsederdi. Oto-seçim yalnız yeni dizayn kartında (BelgeDizayniCreatePage) uygulanır. */}
        <label style={st.tbLabel}>Distribütör</label>
        <select
          style={st.tbSelect}
          value={tpl.vendorId || ''}
          disabled={isDistributorUser}
          title={isDistributorUser
            ? 'Distribütör tipi kullanıcı — kapsam değiştirilemez.'
            : 'Boş bırakılırsa dizayn tüm distribütörler için geçerlidir.'}
          onChange={(e) => setTpl((t) => ({ ...t, vendorId: e.target.value || null }))}
        >
          {scopeOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span style={st.tbSep} />
        <label style={st.tbLabel}>Kağıt</label>
        <select style={st.tbSelect} value={layout.page.paperSize} onChange={(e) => patchPage({ paperSize: e.target.value })}>
          {Object.entries(PAPER_SIZES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <Segmented title="Yön" value={layout.page.orientation} onChange={(v) => patchPage({ orientation: v })}
          options={[{ value: 0, label: 'Dikey' }, { value: 1, label: 'Yatay' }]} />
        <span style={st.tbSep} />
        <label style={st.tbLabel}>Kenar</label>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          {['top', 'right', 'bottom', 'left'].map((side) => (
            <input key={side} type="number" title={`Kenar ${side} (mm)`} style={st.tbNum} value={m[side]}
              onChange={(e) => patchPage({ margins: { [side]: Number(e.target.value) || 0 } })} />
          ))}
        </div>
        <span style={st.tbSep} />
        <button style={{ ...st.iconBtn, ...(showGrid ? st.iconBtnActive : {}) }} onClick={() => setShowGrid((v) => !v)} title="Izgarayı göster/gizle">▦</button>
        <button style={{ ...st.iconBtn, ...(snapGrid ? st.iconBtnActive : {}) }} onClick={() => setSnapGrid((v) => !v)} title="Izgaraya yapış (kapalıyken akıllı kılavuz)">🧲</button>
        <select style={st.tbSelect} value={gridMm} onChange={(e) => setGridMm(Number(e.target.value))} title="Izgara adımı">
          {[2, 5, 10].map((g) => <option key={g} value={g}>{g}mm</option>)}
        </select>
        <span style={st.tbSep} />
        <button style={st.iconBtn} onClick={() => setZoom(zoom / 1.1)} title="Uzaklaş">−</button>
        <span style={{ ...st.tbLabel, minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button style={st.iconBtn} onClick={() => setZoom(zoom * 1.1)} title="Yakınlaş">+</button>
        <button style={st.iconBtn} onClick={zoomTo100} title="Gerçek boyut (%100)">1:1</button>
        <button style={st.iconBtn} onClick={fitToScreen} title="Ekrana sığdır">⤢</button>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          <button
            style={{ ...s.btnSecondary, ...(aiOpen ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}
            onClick={() => setAiOpen((v) => !v)}
            title="Rona AI ile tasarla (görselden / sohbetle)"
          >✦ Rona AI</button>
          <button style={s.btnSecondary} onClick={() => setXsltOpen(true)} title="Bir e-belge XSLT dosyasını dizayna çevir">XSLT İçe Aktar</button>
          <button style={s.btnSecondary} onClick={printBlank}>Boş Yazdır</button>
          <button style={s.btnSecondary} onClick={openPreview}>Önizle / Yazdır</button>
          {canEdit && <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>}
        </span>
      </div>

      <div style={st.body}>
        {/* Sol panel — Öğeler / Katmanlar */}
        <div style={st.side}>
          <div style={st.sideTabs}>
            <button style={st.sideTab(sideTab === 'elements')} onClick={() => setSideTab('elements')}>Öğeler</button>
            <button style={st.sideTab(sideTab === 'layers')} onClick={() => setSideTab('layers')}>Katmanlar ({layout.elements.length})</button>
          </div>
          <div style={st.sideScroll}>
            {sideTab === 'elements' ? (
              <>
                <div style={st.paletteHead}>Ekle</div>
                <div style={st.toolGrid}>
                  {TOOL_ITEMS.map((t) => (
                    <button key={t.kind} style={st.toolCard} title={t.title || t.label} onClick={() => addStatic(t.kind)}>
                      <span style={st.toolCardIcon}>{elementIcon(t.kind)}</span>
                      {t.label}
                    </button>
                  ))}
                </div>
                {Object.entries(SOURCE_FIELDS).map(([key, group]) => (
                  <details key={key} style={st.acc} open={key === 'Customer' || key === 'Invoice'}>
                    <summary style={st.accSummary}>{group.label}{group.isDetail ? ' • detay' : ''}</summary>
                    <div style={st.accBody}>
                      {group.fields.map((f) => (
                        <button key={f.key} style={st.fieldChip} title={`${key}.${f.key}`} onClick={() => addFieldElement(key, f)}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </details>
                ))}
              </>
            ) : (
              <LayersPanel
                elements={layout.elements}
                selIds={selIds}
                onSelect={(elId, additive) => selectElement(elId, additive)}
                onToggleLock={toggleLock}
                onToggleHidden={toggleHidden}
                onReorder={onLayerReorder}
              />
            )}
          </div>
        </div>

        {/* Tuval + cetveller */}
        <div style={st.stage}>
          <div style={st.corner} />
          <RulerTop originX={rulerOrigin.x} lengthMm={dims.widthMm} zoom={zoom} />
          <RulerLeft originY={rulerOrigin.y} lengthMm={dims.heightMm} zoom={zoom} />
          <div
            ref={canvasWrapRef}
            style={{ ...st.canvasWrap, cursor: panning ? 'grabbing' : (spaceHeld ? 'grab' : 'default') }}
            onScroll={syncRulerOrigin}
            onMouseDown={(e) => { if (onPanMouseDown(e)) return; if (e.target === e.currentTarget) clearSelection(); }}
          >
            <div style={st.canvasInner} onMouseDown={(e) => { if (e.target === e.currentTarget) clearSelection(); }}>
              <div ref={pageRef} style={{ ...st.page, width: mmToPx(dims.widthMm), height: mmToPx(dims.heightMm) }}
                onMouseDown={(e) => { if (e.target === e.currentTarget) clearSelection(); }}>
                {/* Yazdırılabilir alan */}
                <div
                  onMouseDown={(e) => { if (e.target === e.currentTarget) clearSelection(); }}
                  style={{
                    position: 'absolute', left: mmToPx(m.left), top: mmToPx(m.top),
                    width: mmToPx(printW), height: mmToPx(printH),
                    border: '1px dashed var(--border-color)', boxSizing: 'border-box',
                  }}>
                  {showGrid && <GridOverlay gridMm={gridMm} zoom={zoom} widthPx={mmToPx(printW)} heightPx={mmToPx(printH)} />}

                  {/* Detay bandı — serbest öğelerin ALTINDA çizilir ki üstüne konan çizgi/toplam
                      bandın gri kutusunun arkasında kalıp tıklanamaz hâle gelmesin. */}
                  {view.detailBand?.enabled && (
                    <Rnd
                      bounds="parent"
                      disableDragging={spaceHeld}
                      size={{ width: mmToPx(view.detailBand.width), height: mmToPx(Math.max(view.detailBand.rowHeight * 2, 12)) }}
                      position={{ x: mmToPx(view.detailBand.x), y: mmToPx(view.detailBand.y) }}
                      onDragStop={(e, d) => updateBand({ x: round(pxToMm(d.x)), y: round(pxToMm(d.y)) })}
                      onResizeStop={(e, dir, ref) => updateBand({ width: round(pxToMm(parseFloat(ref.style.width))) })}
                      onMouseDown={() => { setSelIds([]); setSel({ kind: 'band' }); }}
                      style={{
                        border: sel?.kind === 'band' ? '1px solid var(--accent)' : '1px dashed var(--text-secondary)',
                        background: 'var(--designer-band)', boxSizing: 'border-box', cursor: 'move',
                        zIndex: 0,
                      }}
                    >
                      <DetailBandPreview band={view.detailBand} zoom={zoom} onSelectCol={(colId) => { setSelIds([]); setSel({ kind: 'bandcol', id: colId }); }} selColId={sel?.kind === 'bandcol' ? sel.id : null} />
                      {/* Baskıda detay satırları çoğaldıkça band aşağı uzar. */}
                      <div style={{ position: 'absolute', left: 2, bottom: -16, fontSize: 9, color: 'var(--text-secondary)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                        ↧ baskıda satırlar buradan aşağı uzar — altındaki öğeler (dip toplam vb.) otomatik kayar
                      </div>
                    </Rnd>
                  )}

                  {view.elements.map((el) => {
                    const inSel = selIds.includes(el.id);
                    if (el.hidden && !inSel) return null; // gizli öğe (seçili değilse) çizilmez
                    const isPrimary = sel?.kind === 'element' && sel.id === el.id;
                    const isLine = el.type === 'line';
                    // Çizgi çok ince → gövdeyi tutulabilir kılmak için min 14px sürükleme alanı.
                    const HIT = 14;
                    const dragging = drag?.id === el.id;
                    return (
                    <Rnd
                      key={el.id}
                      bounds="parent"
                      disableDragging={!!el.locked || spaceHeld}
                      size={{ width: mmToPx(el.width), height: isLine ? Math.max(mmToPx(el.height || 0.3), HIT) : mmToPx(el.height || 1) }}
                      position={dragging ? { x: mmToPx(drag.x), y: mmToPx(drag.y) } : { x: mmToPx(el.x), y: mmToPx(el.y) }}
                      enableResizing={el.locked ? false : (isLine ? LINE_ENABLE_RESIZE : undefined)}
                      resizeHandleStyles={!inSel || el.locked ? undefined : (isLine ? LINE_RESIZE_STYLES : EL_RESIZE_STYLES)}
                      onDrag={(e, d) => onElementDrag(el, d)}
                      onDragStop={(e, d) => onElementDragStop(el, d)}
                      onResize={(e, dir, ref, delta, pos) => onElementResize(el, dir, ref, pos)}
                      onResizeStop={(e, dir, ref, delta, pos) => onElementResizeStop(el, dir, ref, pos, isLine)}
                      onMouseDown={(e) => onElementMouseDown(e, el.id)}
                      style={{
                        border: inSel ? `1px solid ${isPrimary ? 'var(--accent)' : 'var(--accent)'}` : '1px solid transparent',
                        background: inSel ? 'var(--accent-soft)' : 'transparent',
                        boxShadow: inSel && !isPrimary ? 'var(--focus-ring)' : 'none',
                        opacity: el.hidden ? 0.4 : 1,
                        cursor: el.locked ? 'default' : 'move', boxSizing: 'border-box', zIndex: 1,
                      }}
                    >
                      <ElementPreview el={el} zoom={zoom} />
                      {el.locked && (
                        <div title="Kilitli" style={{ position: 'absolute', top: -2, right: -2, fontSize: 9, lineHeight: 1, background: 'var(--text-secondary)', color: '#fff', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none' }}>🔒</div>
                      )}
                      {el.followDetail === true && (
                        <div title="Baskıda detay bandını takip eder (elle ayarlandı)" style={{ position: 'absolute', top: -2, left: -2, fontSize: 9, lineHeight: 1, background: 'var(--accent-2)', color: '#fff', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none' }}>↧</div>
                      )}
                      {el.followDetail === false && (
                        <div title="Baskıda sabit konum (elle ayarlandı)" style={{ position: 'absolute', top: -2, left: -2, fontSize: 9, lineHeight: 1, background: 'var(--text-secondary)', color: '#fff', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none' }}>📌</div>
                      )}
                    </Rnd>
                    );
                  })}

                  {/* Akıllı hizalama kılavuzları */}
                  <SmartGuides guides={guides} mmToPx={mmToPx} widthPx={mmToPx(printW)} heightPx={mmToPx(printH)} />

                  {/* Hayalet imleç: akış boyunca kalır, canlı çizilen son elemanı takip eder */}
                  {aiThinking && (
                    <DesignerGhostOverlay
                      thinking={aiThinking}
                      liveElements={livePreview?.elements || []}
                      widthPx={mmToPx(printW)}
                      heightPx={mmToPx(printH)}
                      mmToPx={mmToPx}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sağ özellik paneli */}
        <div style={st.props}>
          <div style={st.propsHead}>Özellikler</div>
          <div style={{ padding: 12, overflowY: 'auto' }}>
            {selIds.length >= 2 && (
              <MultiSelectProps count={selIds.length} onAlign={alignElements} onDelete={deleteSelected} onZ={applyZ} />
            )}
            {selIds.length < 2 && selectedElement && (
              <ElementProps
                el={selectedElement}
                onChange={(patch) => updateElement(selectedElement.id, patch)}
                onDelete={deleteSelected}
                onAlign={alignElements}
                onZ={applyZ}
                onToggleLock={() => toggleLock(selectedElement.id)}
                onToggleHidden={() => toggleHidden(selectedElement.id)}
              />
            )}
            {selIds.length < 2 && selectedCol && (
              <BandColProps col={selectedCol} onChange={(patch) => updateBandCol(selectedCol.id, patch)} onDelete={deleteSelected} />
            )}
            {sel?.kind === 'band' && (
              <BandProps band={layout.detailBand} onChange={updateBand} />
            )}
            {(!sel || sel.kind === 'page') && (
              <BandToggle band={layout.detailBand} onChange={updateBand} />
            )}
          </div>
        </div>

        {/* AI tasarım paneli — özellik panelinin SAĞINA açılır; kapanınca gizlenir (sohbet geçmişi korunur). */}
        <AiDesignPanel
          open={aiOpen}
          docType={tpl.docType}
          layout={layout}
          onThinking={setAiThinking}
          onLivePreview={onAiLivePreview}
          onPlan={onAiFinal}
          onClose={() => setAiOpen(false)}
          showToast={showToast}
        />
      </div>

      {/* Önizleme modalı */}
      {previewOpen && (
        <div style={s.overlay} onClick={() => setPreviewOpen(false)}>
          <div style={{ ...s.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={s.modalTitle}>Önizleme — Belge Seç</h3></div>
            <div style={{ ...s.modalBody, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: 13 }}>
                Alanların gerçek değerlerle dolması için bir {docTypeLabel} belgesi seçin. Boş yazdırmak için modalı kapatıp “Boş Yazdır” kullanın.
              </p>
              <select style={s.select} value={previewDocId} onChange={(e) => setPreviewDocId(e.target.value)}>
                <option value="">Belge seçin…</option>
                {docList.map((d) => (
                  <option key={d.id} value={d.id}>
                    {(d.textId || d.printedNo || d.documentNo || d.id)} {(d.documentDate || d.paymentDate || d.transactionDate) ? `— ${new Date(d.documentDate || d.paymentDate || d.transactionDate).toLocaleDateString('tr-TR')}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={() => runPreview(true)}>Yazdır</button>
              <button style={s.btnSecondary} onClick={() => setPreviewOpen(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* XSLT içe aktarma diyaloğu (Talep #153) */}
      <XsltImportDialog
        open={xsltOpen}
        onClose={() => setXsltOpen(false)}
        onImport={(elements, detailBand, meta) => applyXsltImport(elements, detailBand, meta)}
        printWidthMm={printW}
        hasExisting={(layout?.elements?.length || 0) > 0}
      />

      <Toast toast={toast} />
    </div>
  );
}

function ElementPreview({ el }) {
  const opacity = el.opacity != null && el.opacity < 1 ? el.opacity : undefined;
  if (el.type === 'line') return <div style={{ width: '100%', borderTop: `${Math.max(1, el.height * PX_PER_MM)}px solid ${el.color || '#000'}`, position: 'absolute', top: 0, opacity }} />;
  if (el.type === 'box') {
    return <div style={{
      width: '100%', height: '100%',
      border: `${Math.max(1, (el.borderWidth || 0.3) * PX_PER_MM)}px solid ${el.borderColor || '#000'}`,
      background: el.bgColor || 'transparent',
      borderRadius: el.radius ? `${el.radius * PX_PER_MM}px` : 0,
      boxSizing: 'border-box', opacity,
    }} />;
  }
  if (el.type === 'image') {
    const brd = el.border ? `${Math.max(1, (el.borderWidth || 0.3) * PX_PER_MM)}px solid ${el.borderColor || '#000'}` : undefined;
    const radius = el.radius ? `${el.radius * PX_PER_MM}px` : 0;
    return el.src
      ? <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain', display: 'block', pointerEvents: 'none', border: brd, borderRadius: radius, boxSizing: 'border-box', opacity }} />
      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: brd || '1px dashed #94a3b8', borderRadius: radius, color: '#64748b', fontSize: 11, boxSizing: 'border-box', opacity }}>🖼 Resim</div>;
  }
  if (el.type === 'barcode') {
    return <div style={{ width: '100%', height: '100%', opacity }}><BarcodePreview el={el} /></div>;
  }
  const wrap = el.type === 'amount-words' || el.wrap;
  let text;
  if (el.type === 'static-text') text = el.text || 'Metin';
  else if (el.type === 'calc' || el.type === 'amount-words') text = resolveElementValue(el, null);
  else text = `[${(SOURCE_FIELDS[el.source]?.fields.find((f) => f.key === el.fieldKey) || {}).label || el.fieldKey}]`;
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', whiteSpace: wrap ? 'normal' : 'nowrap',
      fontSize: `${(el.fontSizePt || 10) * 1.33}px`,
      fontWeight: el.bold ? 'bold' : 'normal',
      textDecoration: el.underline ? 'underline' : 'none',
      fontStyle: el.italic ? 'italic' : 'normal',
      textAlign: el.align || 'left',
      display: 'flex', alignItems: el.valign === 'middle' ? 'center' : el.valign === 'bottom' ? 'flex-end' : 'flex-start',
      justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start',
      color: el.color || '#111',
      background: el.bgColor || 'transparent',
      border: el.border ? `${Math.max(1, (el.borderWidth || 0.3) * PX_PER_MM)}px solid ${el.borderColor || '#000'}` : undefined,
      borderRadius: el.radius ? `${el.radius * PX_PER_MM}px` : 0,
      padding: el.border ? '0 2px' : 0, boxSizing: 'border-box', lineHeight: 1.1, opacity,
    }}>{text}</div>
  );
}

function BarcodePreview({ el }) {
  const html = useMemo(() => {
    try { return barcodeHtml(resolveBarcodeValue(el, null), el.format, el.showText); } catch { return ''; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el.format, el.showText, el.valueMode, el.text, el.source, el.fieldKey]);
  return (
    <div
      style={{ width: '100%', height: '100%', overflow: 'hidden', pointerEvents: 'none' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function DetailBandPreview({ band, onSelectCol, selColId }) {
  const cols = band.columns || [];
  const footerLabel = (c) => {
    if (c.summary && c.summary !== 'none') return `${AGG_SHORT[c.summary] || c.summary} …`;
    return c.footerText || '';
  };
  const bandBorder = band.borderColor ? `${Math.max(1, (band.borderWidth || 0.3) * PX_PER_MM)}px solid ${band.borderColor}` : undefined;
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', fontSize: 10, overflow: 'hidden', border: bandBorder, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0, background: band.showHeader && band.headerBgColor ? band.headerBgColor : 'transparent' }}>
        {cols.map((c) => (
          <div key={c.id}
            onMouseDown={(e) => { e.stopPropagation(); onSelectCol(c.id); }}
            style={{
              width: `${(c.width / band.width) * 100}%`, borderRight: '1px dotted #cbd5e1',
              background: selColId === c.id ? 'rgba(0,115,234,0.12)' : 'transparent',
              padding: '1px 2px', overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'pointer',
              textAlign: c.align || 'left', fontWeight: c.bold ? 'bold' : 'normal',
            }}>
            {c.headerText || c.fieldKey}
          </div>
        ))}
        {!cols.length && <div style={{ padding: 4, color: '#64748b' }}>Detay alanı sürükleyin →</div>}
      </div>
      {band.showFooter && cols.length > 0 && (
        <div style={{ display: 'flex', borderTop: '1px solid #475569', background: 'rgba(71,85,105,0.06)' }}>
          {cols.map((c) => (
            <div key={c.id}
              onMouseDown={(e) => { e.stopPropagation(); onSelectCol(c.id); }}
              style={{
                width: `${(c.width / band.width) * 100}%`, borderRight: '1px dotted #cbd5e1',
                padding: '1px 2px', overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'pointer',
                textAlign: c.align || 'left', fontWeight: 'bold', color: '#334155',
              }}>
              {footerLabel(c)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Özellik panelleri ---- (pf/plabel/pin/psel/toggleBtn → designerStyles.js)
function NumRow({ label, value, onChange, step = 1, disabled }) {
  // Kontrollü number input'ta ham metni tut: "0." / "-" gibi ara durumlar silinmesin,
  // böylece 0.3 gibi küçük ondalıklar yazılabilir (çizgi kalınlığı vb.).
  const [text, setText] = useState(String(value ?? 0));
  useEffect(() => { setText((prev) => (Number(prev) === Number(value) ? prev : String(value ?? 0))); }, [value]);
  const onInput = (t) => {
    setText(t);
    if (t === '' || t === '-' || t === '.' || t.endsWith('.')) return; // ara durum: commit etme
    const n = Number(t);
    if (Number.isFinite(n)) onChange(n);
  };
  return (
    <div style={pf}>
      <label style={plabel}>{label}</label>
      <input type="number" step={step} style={{ ...pin, opacity: disabled ? 0.5 : 1 }} value={text} disabled={disabled} onChange={(e) => onInput(e.target.value)} />
    </div>
  );
}

function ColorRow({ label, value, onChange, allowNone = false, defaultColor = '#000000' }) {
  return (
    <div style={pf}>
      <label style={plabel}>{label}</label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="color" value={value || defaultColor} onChange={(e) => onChange(e.target.value)} style={swatch} title="Renk seç" />
        <input style={{ ...pin, flex: 1 }} value={value || ''} placeholder={allowNone ? 'yok' : defaultColor} onChange={(e) => onChange(e.target.value)} />
        {allowNone && value ? <button style={{ ...s.btnSecondary, padding: '4px 8px' }} onClick={() => onChange('')} title="Dolguyu kaldır">✕</button> : null}
      </div>
    </div>
  );
}

// Dolgu / kenarlık rengi / köşe yuvarlama — eleman tipine göre.
function AppearanceControls({ el, onChange }) {
  const isText = el.type === 'field' || el.type === 'static-text' || el.type === 'calc' || el.type === 'amount-words';
  const isBox = el.type === 'box';
  const isImage = el.type === 'image';
  const showBorderStyle = isBox || ((isText || isImage) && el.border);
  return (
    <div style={sectionWrap}>
      <div style={sectionTitle}>Görünüm</div>
      {(isText || isBox) && <ColorRow label="Dolgu (arka plan)" value={el.bgColor} onChange={(v) => onChange({ bgColor: v })} allowNone />}
      {showBorderStyle && (
        <>
          <ColorRow label="Kenarlık Rengi" value={el.borderColor} onChange={(v) => onChange({ borderColor: v })} />
          <NumRow label="Kenarlık Kalınlığı (mm)" value={el.borderWidth ?? 0.3} step={0.1} onChange={(v) => onChange({ borderWidth: v })} />
        </>
      )}
      {(isText || isBox || isImage) && <NumRow label="Köşe Yuvarlama (mm)" value={el.radius ?? 0} step={0.5} onChange={(v) => onChange({ radius: v })} />}
    </div>
  );
}

function StyleControls({ obj, onChange }) {
  return (
    <>
      <NumRow label="Punto (pt)" value={obj.fontSizePt || 10} onChange={(v) => onChange({ fontSizePt: v })} />
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button style={toggleBtn(obj.bold)} onClick={() => onChange({ bold: !obj.bold })} title="Kalın"><b>B</b></button>
        <button style={toggleBtn(obj.underline)} onClick={() => onChange({ underline: !obj.underline })} title="Altı çizili"><u>U</u></button>
        <button style={toggleBtn(obj.italic)} onClick={() => onChange({ italic: !obj.italic })} title="İtalik"><i>I</i></button>
      </div>
      <div style={pf}>
        <label style={plabel}>Hizalama</label>
        <select style={psel} value={obj.align || 'left'} onChange={(e) => onChange({ align: e.target.value })}>
          {ALIGNS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      <ColorRow label="Metin Rengi" value={obj.color} onChange={(v) => onChange({ color: v })} />
    </>
  );
}

function AlignToolbar({ count, onAlign }) {
  const scope = count >= 2 ? 'seçime göre' : 'sayfaya göre';
  const Btn = ({ mode, label, title, disabled }) => (
    <button title={title} disabled={disabled} style={{ ...alignBtn, opacity: disabled ? 0.4 : 1 }} onClick={() => onAlign(mode)}>{label}</button>
  );
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={plabel}>Hizalama ({scope})</label>
      <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
        <Btn mode="left" label="⊣ Sol" title="Sola hizala" />
        <Btn mode="hcenter" label="↔ Orta" title="Yatay ortala" />
        <Btn mode="right" label="Sağ ⊢" title="Sağa hizala" />
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        <Btn mode="top" label="⊤ Üst" title="Üste hizala" />
        <Btn mode="vcenter" label="↕ Orta" title="Dikey ortala" />
        <Btn mode="bottom" label="Alt ⊥" title="Alta hizala" />
      </div>
      {count >= 2 && (
        <>
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <Btn mode="distH" label="Yatay dağıt" title="Yatay eşit aralık (3+ öğe)" disabled={count < 3} />
            <Btn mode="distV" label="Dikey dağıt" title="Dikey eşit aralık (3+ öğe)" disabled={count < 3} />
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <Btn mode="sameW" label="Eşit genişlik" title="İlk seçilenle aynı genişlik" />
            <Btn mode="sameH" label="Eşit yükseklik" title="İlk seçilenle aynı yükseklik" />
          </div>
        </>
      )}
    </div>
  );
}

// Z-sıra düğmeleri (öne/arkaya). onZ bir reorder fonksiyonu alır.
function ZOrderBar({ onZ }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={plabel}>Sıralama (z)</label>
      <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
        <button style={alignBtn} title="En öne (Ctrl+Shift+])" onClick={() => onZ(toFront)}>⤒ En öne</button>
        <button style={alignBtn} title="Öne (Ctrl+])" onClick={() => onZ(bringForward)}>↑ Öne</button>
        <button style={alignBtn} title="Arkaya (Ctrl+[)" onClick={() => onZ(sendBackward)}>↓ Arkaya</button>
        <button style={alignBtn} title="En arkaya (Ctrl+Shift+[)" onClick={() => onZ(toBack)}>⤓ En arka</button>
      </div>
    </div>
  );
}

function MultiSelectProps({ count, onAlign, onDelete, onZ }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12, color: 'var(--text-primary)' }}>{count} öğe seçili</div>
      <AlignToolbar count={count} onAlign={onAlign} />
      {onZ && <ZOrderBar onZ={onZ} />}
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0 }}>
        Ok tuşlarıyla taşıyın (Shift = 5 mm). Eşitleme “ilk seçilen” öğeyi referans alır. Bir öğeyi sürüklemek grubu birlikte taşır. Kopyala/Yapıştır: Ctrl+C / Ctrl+V, Çoğalt: Ctrl+D.
      </p>
      <button style={{ ...s.btnDanger, width: '100%', marginTop: 8 }} onClick={onDelete}>Seçili {count} Öğeyi Sil</button>
    </div>
  );
}

const SOURCE_OPTS = Object.entries(SOURCE_FIELDS).filter(([, g]) => !g.isDetail);

function ImageControls({ el, onChange }) {
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      // LayoutJson DB'de saklanıp mobile senkronlandığı için büyük görselleri uyar.
      if (url.length > 700 * 1024) {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Bu görsel oldukça büyük (~' + Math.round(url.length / 1024) + ' KB). Belge boyutunu artırır. Yine de ekleyeyim mi?')) return;
      }
      onChange({ src: url });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  return (
    <>
      <div style={pf}>
        <label style={plabel}>Resim URL'si</label>
        <input style={pin} placeholder="https://… veya yükleyin" value={el.src && el.src.startsWith('data:') ? '(yüklenen görsel)' : (el.src || '')}
          onChange={(e) => onChange({ src: e.target.value })} disabled={el.src && el.src.startsWith('data:')} />
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <label style={{ ...s.btnSecondary, padding: '6px 10px', cursor: 'pointer', flex: 1, textAlign: 'center' }}>
          Dosya Yükle
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
        </label>
        {el.src && <button style={{ ...s.btnSecondary, padding: '6px 10px' }} onClick={() => onChange({ src: '' })}>Temizle</button>}
      </div>
      <div style={pf}>
        <label style={plabel}>Sığdırma</label>
        <select style={psel} value={el.fit || 'contain'} onChange={(e) => onChange({ fit: e.target.value })}>
          <option value="contain">İçine sığdır (oran korunur)</option>
          <option value="fill">Doldur (oran bozulur)</option>
          <option value="cover">Kapla (kırpılır)</option>
        </select>
      </div>
      <div style={pf}>
        <label style={plabel}>Kenarlık</label>
        <button style={toggleBtn(el.border)} onClick={() => onChange({ border: !el.border })}>{el.border ? 'Açık' : 'Kapalı'}</button>
      </div>
    </>
  );
}

function BarcodeControls({ el, onChange }) {
  const fields = (SOURCE_FIELDS[el.source]?.fields) || [];
  return (
    <>
      <div style={pf}>
        <label style={plabel}>Barkod Türü</label>
        <select style={psel} value={el.format || 'CODE128'} onChange={(e) => onChange({ format: e.target.value })}>
          {BARCODE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      <div style={pf}>
        <label style={plabel}>Değer Kaynağı</label>
        <select style={psel} value={el.valueMode || 'static'} onChange={(e) => onChange({ valueMode: e.target.value })}>
          <option value="static">Sabit metin</option>
          <option value="field">Belge alanı</option>
        </select>
      </div>
      {el.valueMode === 'field' ? (
        <>
          <div style={pf}>
            <label style={plabel}>Kayıt</label>
            <select style={psel} value={el.source || 'Invoice'} onChange={(e) => onChange({ source: e.target.value, fieldKey: (SOURCE_FIELDS[e.target.value]?.fields[0]?.key) || '' })}>
              {SOURCE_OPTS.map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
            </select>
          </div>
          <div style={pf}>
            <label style={plabel}>Alan</label>
            <select style={psel} value={el.fieldKey || ''} onChange={(e) => onChange({ fieldKey: e.target.value })}>
              {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
        </>
      ) : (
        <div style={pf}>
          <label style={plabel}>Sabit Değer</label>
          <input style={pin} value={el.text || ''} onChange={(e) => onChange({ text: e.target.value })} />
        </div>
      )}
      {el.format !== 'QR' && (
        <div style={pf}>
          <label style={plabel}>Rakamları Göster</label>
          <button style={toggleBtn(el.showText)} onClick={() => onChange({ showText: !el.showText })}>{el.showText ? 'Açık' : 'Kapalı'}</button>
        </div>
      )}
    </>
  );
}

const DETAIL_AGG_FIELDS = (SOURCE_FIELDS.InvoiceDetail?.fields || []).filter((f) => f.type === 'money' || f.type === 'number');

function CalcControls({ el, onChange }) {
  return (
    <>
      <div style={pf}>
        <label style={plabel}>İşlem</label>
        <select style={psel} value={el.aggFn || 'sum'} onChange={(e) => onChange({ aggFn: e.target.value })}>
          {AGG_FUNCS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      {el.aggFn !== 'count' && (
        <div style={pf}>
          <label style={plabel}>Detay Alanı</label>
          <select style={psel} value={el.aggField || ''} onChange={(e) => onChange({ aggField: e.target.value })}>
            {DETAIL_AGG_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
      )}
      <div style={pf}>
        <label style={plabel}>Biçim</label>
        <select style={psel} value={el.valueType || 'money'} onChange={(e) => onChange({ valueType: e.target.value })}>
          <option value="money">Para (1.234,56)</option>
          <option value="number">Sayı</option>
        </select>
      </div>
      <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px' }}>
        Belge kalemleri (detay) üzerinden hesaplanır. Örn. tüm satırların “Net” toplamı = genel toplam.
      </p>
    </>
  );
}

function AmountWordsControls({ el, onChange }) {
  const fields = (SOURCE_FIELDS[el.source]?.fields || []).filter((f) => f.type === 'money');
  return (
    <>
      <div style={pf}>
        <label style={plabel}>Kayıt</label>
        <select style={psel} value={el.source || 'Invoice'} onChange={(e) => {
          const first = (SOURCE_FIELDS[e.target.value]?.fields || []).find((f) => f.type === 'money');
          onChange({ source: e.target.value, fieldKey: first?.key || '' });
        }}>
          {SOURCE_OPTS.map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
        </select>
      </div>
      <div style={pf}>
        <label style={plabel}>Tutar Alanı</label>
        <select style={psel} value={el.fieldKey || ''} onChange={(e) => onChange({ fieldKey: e.target.value })}>
          {fields.length ? fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)
            : <option value="">(bu kayıtta para alanı yok)</option>}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={pf}>
          <label style={plabel}>Para Birimi</label>
          <input style={pin} value={el.currency ?? 'TL'} onChange={(e) => onChange({ currency: e.target.value })} />
        </div>
        <div style={pf}>
          <label style={plabel}>Kuruş Eki</label>
          <input style={pin} value={el.subunit ?? 'Kr'} onChange={(e) => onChange({ subunit: e.target.value })} />
        </div>
      </div>
      <div style={pf}>
        <label style={plabel}>Büyük Harf</label>
        <button style={toggleBtn(el.upper)} onClick={() => onChange({ upper: !el.upper })}>{el.upper ? 'Açık' : 'Kapalı'}</button>
      </div>
      <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px' }}>
        Örn. “Bin İki Yüz Elli TL Elli Kr”. Uzun metinler kutu içinde alta sarar — yüksekliği yeterli verin.
      </p>
    </>
  );
}

function ElementProps({ el, onChange, onDelete, onAlign, onZ, onToggleLock, onToggleHidden }) {
  const fieldLabel = el.type === 'field'
    ? `${SOURCE_FIELDS[el.source]?.label} • ${(SOURCE_FIELDS[el.source]?.fields.find((f) => f.key === el.fieldKey) || {}).label || el.fieldKey}`
    : el.type === 'static-text' ? 'Sabit Metin' : el.type === 'line' ? 'Çizgi' : el.type === 'box' ? 'Kutu'
      : el.type === 'image' ? 'Resim' : el.type === 'barcode' ? 'Barkod / QR'
        : el.type === 'calc' ? 'Hesap (Detay Toplamı)' : el.type === 'amount-words' ? 'Yazıyla Tutar' : el.type;
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12, color: 'var(--text-primary)' }}>{fieldLabel}</div>
      {(onToggleLock || onToggleHidden) && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {onToggleLock && <button style={{ ...toggleBtn(el.locked), flex: 1 }} onClick={onToggleLock}>{el.locked ? '🔒 Kilitli' : '🔓 Kilitle'}</button>}
          {onToggleHidden && <button style={{ ...toggleBtn(el.hidden), flex: 1 }} onClick={onToggleHidden}>{el.hidden ? '🚫 Gizli' : '👁 Gizle'}</button>}
        </div>
      )}
      {el.type === 'static-text' && (
        <div style={pf}>
          <label style={plabel}>Metin</label>
          <input style={pin} value={el.text || ''} onChange={(e) => onChange({ text: e.target.value })} />
        </div>
      )}
      {el.type === 'image' && <ImageControls el={el} onChange={onChange} />}
      {el.type === 'barcode' && <BarcodeControls el={el} onChange={onChange} />}
      {el.type === 'calc' && <CalcControls el={el} onChange={onChange} />}
      {el.type === 'amount-words' && <AmountWordsControls el={el} onChange={onChange} />}
      {(el.type === 'field' || el.type === 'static-text' || el.type === 'calc' || el.type === 'amount-words') && <StyleControls obj={el} onChange={onChange} />}
      {el.type === 'line' && <ColorRow label="Çizgi Rengi" value={el.color} onChange={(v) => onChange({ color: v })} />}
      {(el.type === 'field' || el.type === 'static-text' || el.type === 'calc' || el.type === 'amount-words') && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button style={{ ...toggleBtn(el.border), flex: 1 }} onClick={() => onChange({ border: !el.border })} title="Kenarlık">{el.border ? '▢ Kenarlık' : '▢ Kenarlık'}</button>
          <button style={{ ...toggleBtn(el.wrap), flex: 1 }} onClick={() => onChange({ wrap: !el.wrap })} title="Metni birden çok satıra sar">{el.wrap ? '↵ Sar: Açık' : '↵ Sar: Kapalı'}</button>
        </div>
      )}
      {(el.type === 'field' || el.type === 'static-text' || el.type === 'box' || el.type === 'image' || el.type === 'calc' || el.type === 'amount-words') && (
        <AppearanceControls el={el} onChange={onChange} />
      )}
      <div style={pf}>
        <label style={plabel}>Baskı yerleşimi</label>
        <select style={psel}
          value={el.followDetail === true ? 'flow' : el.followDetail === false ? 'fixed' : 'auto'}
          onChange={(e) => onChange({ followDetail: e.target.value === 'flow' ? true : e.target.value === 'fixed' ? false : null })}>
          <option value="auto">Otomatik (önerilen)</option>
          <option value="flow">Detayı takip et (akar)</option>
          <option value="fixed">Sabit konum</option>
        </select>
        <span style={{ fontSize: 11, color: '#64748b' }}>
          Otomatik: bandın altındaki öğeler (dip toplam vb.) baskıda detay satırlarının altına kayar; sayfa dibine yapışık öğeler (imza, alt çizgi) yerinde kalır. Gerekirse elle zorlayabilirsiniz.
        </span>
      </div>
      <NumRow label="Saydamlık (0–1, filigran için)" value={el.opacity != null ? el.opacity : 1} step={0.1} onChange={(v) => onChange({ opacity: Math.max(0, Math.min(1, v)) })} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <NumRow label="X (mm)" value={el.x} step={0.5} disabled={el.locked} onChange={(v) => onChange({ x: v })} />
        <NumRow label="Y (mm)" value={el.y} step={0.5} disabled={el.locked} onChange={(v) => onChange({ y: v })} />
        <NumRow label={el.type === 'line' ? 'Uzunluk (mm)' : 'Genişlik (mm)'} value={el.width} step={0.5} disabled={el.locked} onChange={(v) => onChange({ width: v })} />
        <NumRow label={el.type === 'line' ? 'Kalınlık (mm)' : 'Yükseklik (mm)'} value={el.height} step={el.type === 'line' ? 0.1 : 0.5} disabled={el.locked} onChange={(v) => onChange({ height: v })} />
      </div>
      {onAlign && <div style={{ marginTop: 10 }}><AlignToolbar count={1} onAlign={onAlign} /></div>}
      {onZ && <ZOrderBar onZ={onZ} />}
      <button style={{ ...s.btnDanger, width: '100%', marginTop: 8 }} onClick={onDelete}>Öğeyi Sil</button>
    </div>
  );
}

function BandColProps({ col, onChange, onDelete }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12, color: 'var(--text-primary)' }}>Detay Kolonu: {col.fieldKey}</div>
      <div style={pf}>
        <label style={plabel}>Başlık</label>
        <input style={pin} value={col.headerText || ''} onChange={(e) => onChange({ headerText: e.target.value })} />
      </div>
      <NumRow label="Genişlik (mm)" value={col.width} step={0.5} onChange={(v) => onChange({ width: v })} />
      <StyleControls obj={col} onChange={onChange} />
      <div style={sectionWrap}>
        <div style={sectionTitle}>Toplam Satırı (footer)</div>
        <div style={pf}>
          <label style={plabel}>Bu kolonda</label>
          <select style={psel} value={col.summary || 'none'} onChange={(e) => onChange({ summary: e.target.value })}>
            <option value="none">— (toplam yok)</option>
            {AGG_FUNCS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        {(!col.summary || col.summary === 'none') && (
          <div style={pf}>
            <label style={plabel}>Footer Metni</label>
            <input style={pin} placeholder='örn. "TOPLAM"' value={col.footerText || ''} onChange={(e) => onChange({ footerText: e.target.value })} />
          </div>
        )}
        <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
          Footer satırının görünmesi için Detay Bandı özelliklerinden “Toplam Satırı”nı açın.
        </p>
      </div>
      <button style={{ ...s.btnDanger, width: '100%', marginTop: 8 }} onClick={onDelete}>Kolonu Sil</button>
    </div>
  );
}

function BandProps({ band, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12, color: 'var(--text-primary)' }}>Detay Bandı</div>
      <NumRow label="Satır Yüksekliği (mm)" value={band.rowHeight} step={0.5} onChange={(v) => onChange({ rowHeight: v })} />
      <NumRow label="Genişlik (mm)" value={band.width} step={0.5} onChange={(v) => onChange({ width: v })} />
      <div style={pf}>
        <label style={plabel}>Başlık Satırı</label>
        <button style={toggleBtn(band.showHeader)} onClick={() => onChange({ showHeader: !band.showHeader })}>{band.showHeader ? 'Göster' : 'Gizle'}</button>
      </div>
      <div style={pf}>
        <label style={plabel}>Toplam Satırı (footer)</label>
        <button style={toggleBtn(band.showFooter)} onClick={() => onChange({ showFooter: !band.showFooter })}>{band.showFooter ? 'Göster' : 'Gizle'}</button>
      </div>
      <div style={sectionWrap}>
        <div style={sectionTitle}>Görünüm</div>
        <ColorRow label="Başlık Arka Planı (ör. sarı)" value={band.headerBgColor} onChange={(v) => onChange({ headerBgColor: v })} allowNone />
        <ColorRow label="Çerçeve Rengi" value={band.borderColor} onChange={(v) => onChange({ borderColor: v })} allowNone />
        {band.borderColor ? <NumRow label="Çerçeve Kalınlığı (mm)" value={band.borderWidth ?? 0.3} step={0.1} onChange={(v) => onChange({ borderWidth: v })} /> : null}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Kolon eklemek için soldaki “Belge Kalemleri (Detay)” alanlarını tıklayın. Bir kolonu seçmek için banttaki başlığa tıklayın. Toplam satırını açtıktan sonra ilgili kolonda hangi toplamın görüneceğini kolon özelliklerinden seçin.</p>
    </div>
  );
}

function BandToggle({ band, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12, color: 'var(--text-primary)' }}>Sayfa</div>
      <div style={pf}>
        <label style={plabel}>Detay Bandı</label>
        <button style={toggleBtn(band.enabled)} onClick={() => onChange({ enabled: !band.enabled })}>{band.enabled ? 'Aktif' : 'Pasif'}</button>
      </div>
      <p style={{ fontSize: 12, color: '#64748b' }}>Bir öğe seçtiğinizde özellikleri burada görünür. “Belge Kalemleri (Detay)” alanları detay bandına kolon ekler.</p>
    </div>
  );
}

